import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, RefreshCw, Phone, MessageCircle, ExternalLink, AlertTriangle, Clock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from '../hooks/use-toast';
import api from '../services/api';
import { buildHandoverText, waLinkWithText } from '../utils/parkHandoverText';

const fmtINR = (n) => `₹${(Number(n) || 0).toLocaleString('en-IN')}`;

const parkedDaysBadge = (days, needsAttention) => {
  if (needsAttention) return 'bg-red-100 text-red-700 border-red-300';
  if (days > 5) return 'bg-amber-100 text-amber-700 border-amber-300';
  return 'bg-emerald-50 text-emerald-700 border-emerald-200';
};

const COLUMN_TINT = {
  design:             { head: 'bg-purple-600',  chip: 'bg-purple-50 border-purple-200 text-purple-700' },
  new_stock:          { head: 'bg-blue-600',    chip: 'bg-blue-50 border-blue-200 text-blue-700' },
  order_process:      { head: 'bg-teal-600',    chip: 'bg-teal-50 border-teal-200 text-teal-700' },
  ready_for_delivery: { head: 'bg-emerald-600', chip: 'bg-emerald-50 border-emerald-200 text-emerald-700' },
  customer_approval:  { head: 'bg-amber-600',   chip: 'bg-amber-50 border-amber-200 text-amber-700' },
  _other:             { head: 'bg-gray-600',    chip: 'bg-gray-50 border-gray-200 text-gray-700' },
};

const ParkedBoard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [onlyAttention, setOnlyAttention] = useState(false);

  const load = async (attnOnly) => {
    setLoading(true);
    try {
      const params = attnOnly ? { only_attention: true } : {};
      const res = await api.get('/admin/parked-board', { params });
      setData(res.data);
      if (res.data?.nudges_fired_this_call > 0) {
        toast({
          title: `${res.data.nudges_fired_this_call} nudge${res.data.nudges_fired_this_call === 1 ? '' : 's'} fired`,
          description: 'Salesmen just received a push on their overdue parked leads.',
        });
      }
    } catch (e) {
      toast({
        title: 'Failed to load parked board',
        description: e?.response?.data?.detail || e.message,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(onlyAttention); }, [onlyAttention]);

  if (loading && !data) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-amber-500 border-t-transparent rounded-full animate-spin mx-auto" />
          <p className="text-gray-500 mt-4">Loading parked board…</p>
        </div>
      </div>
    );
  }

  const columns = data?.columns || [];

  return (
    <div className="min-h-screen bg-gray-50" data-testid="parked-board-page">
      {/* Header */}
      <div className="bg-amber-600 text-white sticky top-0 z-10">
        <div className="px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate(-1)}
              className="text-white hover:bg-amber-700"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-lg font-semibold">Parked Board</h1>
              <p className="text-xs text-amber-100" data-testid="parked-board-summary">
                {data?.grand_total ?? 0} parked · {data?.grand_attention ?? 0} need attention
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setOnlyAttention((v) => !v)}
              className={`text-xs px-3 py-1.5 rounded-full border font-semibold transition-all ${
                onlyAttention ? 'bg-white text-amber-700 border-white' : 'bg-transparent text-white border-white/60'
              }`}
              data-testid="toggle-only-attention"
            >
              <AlertTriangle className="w-3.5 h-3.5 inline-block mr-1" />
              Attention only
            </button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => load(onlyAttention)}
              className="text-white hover:bg-amber-700"
              data-testid="refresh-btn"
              disabled={loading}
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>
      </div>

      {/* Columns — responsive: horizontal scroll on mobile, grid on desktop */}
      <div className="p-3 lg:p-4">
        {columns.length === 0 || (data?.grand_total || 0) === 0 ? (
          <div className="bg-white rounded-xl shadow p-10 text-center text-gray-500" data-testid="empty-state">
            <Clock className="w-10 h-10 text-gray-300 mx-auto mb-3" />
            <p className="text-sm">No parked leads right now. Nice work!</p>
          </div>
        ) : (
          <div className="flex gap-3 overflow-x-auto lg:grid lg:grid-cols-3 xl:grid-cols-5 lg:overflow-visible pb-3">
            {columns.map((col) => {
              const tint = COLUMN_TINT[col.key] || COLUMN_TINT._other;
              return (
                <div
                  key={col.key}
                  className="flex-shrink-0 w-72 lg:w-auto bg-white rounded-xl shadow border overflow-hidden flex flex-col"
                  data-testid={`parked-column-${col.key}`}
                >
                  <div className={`${tint.head} text-white px-3 py-2.5 flex items-center justify-between`}>
                    <div>
                      <div className="text-sm font-semibold">{col.label}</div>
                      {col.nudge_days != null && (
                        <div className="text-[10px] text-white/80">Nudge in {col.nudge_days}d</div>
                      )}
                    </div>
                    <div className="flex items-center gap-1.5">
                      {col.attention_count > 0 && (
                        <span className="bg-red-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full flex items-center gap-1" data-testid={`attention-count-${col.key}`}>
                          <AlertTriangle className="w-3 h-3" />
                          {col.attention_count}
                        </span>
                      )}
                      <span className="bg-white/25 text-white text-xs font-bold px-2 py-0.5 rounded-full" data-testid={`total-count-${col.key}`}>
                        {col.total}
                      </span>
                    </div>
                  </div>
                  <div className="p-2 space-y-2 max-h-[70vh] overflow-y-auto">
                    {col.cards.length === 0 && (
                      <div className="text-xs text-gray-400 text-center py-6">No leads here</div>
                    )}
                    {col.cards.map((card) => (
                      <div
                        key={card.lead_id}
                        className={`rounded-lg border p-2.5 text-xs ${
                          card.needs_attention ? 'border-red-300 bg-red-50' : 'border-gray-200 bg-white'
                        }`}
                        data-testid={`parked-card-${card.lead_id}`}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0 flex-1">
                            <div className="font-semibold text-gray-900 truncate">{card.client_name}</div>
                            <div className="text-gray-500 truncate">{card.product_name}</div>
                          </div>
                          <div className="text-right">
                            <div className="font-semibold text-gray-800">{fmtINR(card.estimated_value)}</div>
                          </div>
                        </div>
                        <div className="mt-2 flex flex-wrap items-center gap-1.5">
                          <span
                            className={`text-[10px] font-semibold px-1.5 py-0.5 rounded border ${card.parked_days === null ? 'bg-gray-100 text-gray-500 border-gray-300' : parkedDaysBadge(card.parked_days, card.needs_attention)}`}
                            data-testid={`parked-days-${card.lead_id}`}
                          >
                            {card.parked_days === null ? 'Legacy · no date' : `${card.parked_days}d parked`}
                          </span>
                          {card.needs_attention && (
                            <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded border bg-red-500 text-white border-red-500" data-testid={`attention-flag-${card.lead_id}`}>
                              Needs attention
                            </span>
                          )}
                          {card.follow_up_overdue_days > 0 && (
                            <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded border bg-orange-100 text-orange-700 border-orange-300">
                              Follow-up +{card.follow_up_overdue_days}d
                            </span>
                          )}
                        </div>
                        {card.reason && (
                          <div className="mt-1 text-[11px] text-gray-500 truncate" title={card.reason}>
                            <span className="text-gray-400">Reason: </span>{card.reason}
                          </div>
                        )}
                        <div className="mt-1.5 text-[11px] text-gray-500 truncate">
                          <span className="text-gray-400">Salesman: </span>{card.salesman_name || '—'}
                        </div>
                        {card.last_nudged_at && (
                          <div className="mt-1 text-[10px] text-amber-700">
                            Last nudged {new Date(card.last_nudged_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                          </div>
                        )}
                        <div className="mt-2 flex items-center gap-1.5">
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7 flex-1 text-[11px]"
                            onClick={() => navigate(`/lead-sessions/${card.session_id}`)}
                            data-testid={`open-session-${card.lead_id}`}
                          >
                            <ExternalLink className="w-3 h-3 mr-1" /> Open
                          </Button>
                          {card.client_phone && (
                            <>
                              <a href={`tel:${card.client_phone}`} className="inline-flex" data-testid={`call-${card.lead_id}`}>
                                <Button size="sm" variant="outline" className="h-7 w-7 p-0"><Phone className="w-3 h-3" /></Button>
                              </a>
                              <a
                                href={waLinkWithText(card.client_phone, buildHandoverText({
                                  client_name: card.client_name,
                                  product_name: card.product_name,
                                  park_action: card.park_action,
                                }))}
                                target="_blank"
                                rel="noreferrer"
                                className="inline-flex"
                                data-testid={`whatsapp-${card.lead_id}`}
                              >
                                <Button size="sm" variant="outline" className="h-7 w-7 p-0"><MessageCircle className="w-3 h-3" /></Button>
                              </a>
                            </>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default ParkedBoard;
